import {
  ApiSubscriptionStates,
  Card,
  Loading,
  publishEvent,
  SubscriptionType,
  ErrorBoundary,
  validateObjectProperties,
  filterRelevantSubscriptions
} from '@uma/react-commons/dist/index';
import React, { useMemo } from 'react';
import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import {
  Label,
  Link,
  Row,
  Section,
  SubscriptionsContainer
} from 'src/styles/styles';
import { Device } from 'src/types/IDevices';
import { Entity } from 'src/types/IEntities';
import { DeviceDetailsError, ManageDeviceSubscription } from './analytics';
import { OptionsContent } from './styles';

export interface Subscription {
  resourceId: string;
  type: SubscriptionType;
  state: ApiSubscriptionStates;
  devices: { items: Device[] };
  entities: Entity[];
}

export interface SubscriptionsData {
  data: {
    account?: {
      subscriptions?: Subscription[];
    };
  };
  loading: boolean;
  error?: Error;
  refetch: () => void;
}

interface SubscriptionsProps {
  device: Device;
  deviceDetails: string;
  subscriptionsData: SubscriptionsData;
}

const Subscriptions = ({
  device,
  deviceDetails,
  subscriptionsData
}: SubscriptionsProps): React.ReactElement | null => {
  const { translate } = useDependencyManagerContext();

  const { data, loading, error } = subscriptionsData;

  const subscriptions = useMemo(() => {
    return filterRelevantSubscriptions(data?.account?.subscriptions, device, {
      excludeInactive: true
    });
  }, [data?.account?.subscriptions, device]);

  const subscriptionsWithInstantInkEntities = useMemo(() => {
    return subscriptions?.map((subscription: Subscription) => {
      const { entities, type } = subscription;

      const instantInkEntity = entities?.find(
        (entity: Entity) => entity?.type === 'INSTANT_INK'
      );
      const instantInkPaperEntity = entities?.find(
        (entity: Entity) => entity?.type === 'INSTANTPAPER'
      );

      const label = () => {
        return type === SubscriptionType.INSTANT_INK
          ? translate('common.instantInk', 'HP Instant Ink')
          : translate('common.allIn', 'HP All-In Plan');
      };

      const planDescription = () => {
        const plusPaper = instantInkPaperEntity
          ? ` ${translate('common.plusPaper', '+ Paper')}`
          : '';

        return instantInkEntity?.product?.description
          ? `${instantInkEntity.product.description} ${translate(
              'common.pages',
              'Pages'
            )}${plusPaper}`
          : '';
      };

      return (
        <Row key={subscription.resourceId}>
          <Label>{label()}</Label>
          <SubscriptionsContainer>
            {planDescription()}
            <Link
              className="caption"
              href={`/subscriptions/${subscription.resourceId}?type=${type}`}
              onClick={() => {
                publishEvent(ManageDeviceSubscription(deviceDetails));
              }}
            >
              {`${translate('devices.manage', 'Manage')}`}
            </Link>
          </SubscriptionsContainer>
        </Row>
      );
    });
  }, [deviceDetails, subscriptions, translate]);

  if (loading) {
    return (
      <Section
        background="filled"
        border="outlined"
      >
        <Loading />
      </Section>
    );
  }

  if (validateObjectProperties(error)) {
    return (
      <ErrorBoundary
        analyticsEvent={DeviceDetailsError(error?.message)}
        showOutline={true}
        loading={loading}
        onRetry={() => window.location.reload()}
        errorInfo={{
          error,
          context: 'Device Details Subscriptions',
          operation: 'GET_DEVICE_DETAILS_SUBSCRIPTIONS'
        }}
      />
    );
  }

  if (!subscriptions || subscriptions.length <= 0) {
    return null;
  }

  return (
    <Card
      data-testid="subscriptions"
      title={{
        content: translate(
          'devices.subscriptionsTitle',
          'Subscriptions'
        ) as string
      }}
    >
      <OptionsContent>{subscriptionsWithInstantInkEntities}</OptionsContent>
    </Card>
  );
};

export default Subscriptions;
